import React, {useRef, useState, useEffect, useContext, useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Helmet} from "react-helmet-async";
import {
    Checkbox,
    Panel,
    DefaultButton,
    TextField,
    ITextFieldProps,
    ICheckboxProps,
} from "@fluentui/react";
import {ChevronDoubleRight16Regular, Delete24Regular, SparkleFilled} from "@fluentui/react-icons";
import {useId} from "@fluentui/react-hooks";
import readNDJSONStream from "ndjson-readablestream";

import styles from "./Chat.module.css";

import {
    chatApi,
    configApi,
    RetrievalMode,
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    ResponseMessage,
    VectorFieldOptions,
    GPT4VInput,
    SpeechConfig,
    feedbackApi,
    saveToHistory,
    SystemTemplate,
    initializeSemanticRouter,
    checkAdminAccessApi,
    deleteFeedbackApi,
    SelectedFile
} from "../../api";
import {Answer, AnswerError, AnswerLoading} from "../../components/Answer";
import {FeedbackPayload} from "../../components/Answer/AnswerFeedback";
import {SalesforceFilterChips} from "../../components/SalesforceFilterChips";

import {QuestionInput} from "../../components/QuestionInput";
import {ExampleList} from "../../components/Example";
import {UserChatMessage} from "../../components/UserChatMessage";
import {HelpCallout} from "../../components/HelpCallout";
import {
    AnalysisPanel,
    AnalysisPanelTabs,
} from "../../components/AnalysisPanel";
import {SettingsButton} from "../../components/SettingsButton";
import { AdminAccessButton } from "../../components/AdminAccessButton";
import {UploadFile} from "../../components/UploadFile";
import {useLogin, getToken, requireAccessControl} from "../../authConfig";
import {displayFacetValue} from "../../utils/siteNames";
import {VectorSettings} from "../../components/VectorSettings";
import {useMsal} from "@azure/msal-react";
import {TokenClaimsDisplay} from "../../components/TokenClaimsDisplay";
import {GPT4VSettings} from "../../components/GPT4VSettings";
import {LoginContext} from "../../loginContext";
import {ChatHistory} from "../../components/ChatHistory";
import {LanguagePicker} from "../../i18n/LanguagePicker";
import mainImage from "../../assets/Along the LifeTime.png";
import {SemanticRoutingControl} from "../../components/SemanticRouter/SemanticRouter";
import {TemplateForm} from "../../components/SystemPromptExpander";
import {v4 as uuidv4} from "uuid";
import {Button} from "@fluentui/react-components";
import SearchFilter from "../../components/SearchFilter";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {getIdToken} from "../../authConfig";
import {useChatHistory} from "../../hooks/useChatHistory";
import {useAdminCheck} from "../../hooks/useAdminCheck";
import {ChatContext} from "../../chatContext";

/**
 * A functional React component that implements a chat interface with numerous customizable settings
 * and configurations for handling AI-driven responses. This component maintains the state of the chat
 * session, manages API calls, and processes streamed chat responses asynchronously.
 *
 * State Variables:
 * - sessionId: A unique identifier for the chat session.
 * - isConfigPanelOpen: Boolean state to manage the configuration panel's visibility.
 * - promptTemplate: Template string used to format the prompt for the chat.
 * - temperature: Controls the randomness of the AI's responses.
 * - seed: Optional random seed for deterministic responses.
 * - minimumRerankerScore: The minimum score required for re-ranked results to be considered.
 * - minimumSearchScore: The minimum score required for search results.
 * - retrieveCount: Number of items to retrieve during a query.
 * - retrievalMode: Defines the mode of retrieval, e.g., hybrid, text, etc.
 * - useSemanticRanker: Determines if a semantic ranker is used for queries.
 * - shouldStream: Indicates if the responses should be streamed.
 * - useSemanticCaptions: Boolean that dictates if semantic captions should be used.
 * - excludeCategory: String for category exclusion during searches.
 * - useSuggestFollowupQuestions: Enables or disables suggestion of follow-up questions.
 * - vectorFieldList: List of vector field options.
 * - useOidSecurityFilter: Boolean for using OID security filters.
 * - useGroupsSecurityFilter: Boolean to activate group-based security filters.
 * - gpt4vInput: Enum defining the type of GPT-4V input (text, images, etc.).
 * - useGPT4V: Boolean to enable GPT-4V usage.
 *
 * Refs:
 * - lastQuestionRef: Stores the last posed question for reference.
 * - chatMessageStreamEnd: Refers to the end of the chat message stream.
 *
 * Boolean State Variables:
 * - isLoading: Manages the loading state of the component.
 * - isStreaming: Indicates if the data is currently being streamed.
 * - isPlaying: Tracks the playback state of audio responses.
 *
 * Error Handling:
 * - error: Stores any errors that may occur during API interaction.
 *
 * Additional State Variables:
 * - activeCitation: Active citation being processed or displayed.
 * - activeAnalysisPanelTab: Tab currently active in the analysis panel.
 * - selectedAnswer: Index of the currently selected answer.
 * - answers: List of user-initiated questions and their responses.
 * - streamedAnswers: Collection of answers being streamed in real-time.
 * - speechUrls: Array of URLs for speech output.
 *
 * Conditional Displays:
 * - showGPT4VOptions, showSemanticRankerOption, showVectorOption, showUserUpload,
 *   showLanguagePicker, showSpeechInput, showSpeechOutputBrowser, showSpeechOutputAzure:
 *   Boolean flags to control the visibility of various UI options.
 *
 * Audio:
 * - audio: Audio object used for handling playback.
 *
 * Configurations:
 * - speechConfig: Configuration object for managing speech settings and states.
 *
 * Asynchronous Handlers:
 * - saveHistory: Asynchronously saves the chat history if answers exist.
 * - getConfig: Fetches and sets up configuration settings.
 * - handleAsyncRequest: Manages asynchronous question handling and response streaming.
 * - makeFeedbackRequest: Sends feedback about a question to a feedback API.
 * - makeApiRequest: Makes API requests for retrieving AI responses based on user input.
 *
 * Miscellaneous:
 * - client: Client object to handle login-based operations using the MSAL library.
 */
const Chat = () => {
    const {t, i18n} = useTranslation();
    const [sessionId, setSessionId] = useState(uuidv4());
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [temperature, setTemperature] = useState<number>(0.0);
    const [answerMode, setAnswerMode] = useState<string>("quick");
    const [seed, setSeed] = useState<number | null>(null);
    const [minimumRerankerScore, setMinimumRerankerScore] = useState<number>(2.0);
    const [minimumSearchScore, setMinimumSearchScore] = useState<number>(0);
    const [retrieveCount, setRetrieveCount] = useState<number>(15);
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(
        RetrievalMode.Hybrid
    );
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [useSemanticRouting, setUseSemanticRouting] = useState<boolean>(false);
    const [useSemanticTemplate, setUseSemanticTemplate] =
        useState<boolean>(false);

    const [templates, setTemplates] = useState<SystemTemplate[]>([]);

    const handleTemplateUpdate = useCallback((newTemplates: SystemTemplate[]) => {
        setTemplates(newTemplates);
        // If templates are empty, disable semantic template
        if (newTemplates.length === 0) {
            setUseSemanticTemplate(false);
        }
    }, []);

    const [isRouterInitialized, setIsRouterInitialized] =
        useState<boolean>(false);
    const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});

    // Re-run-with-filters bookkeeping. `lastUserQuestion` is the most recent
    // thing the user actually typed (never overwritten by an empty-Send re-run).
    // `filtersAtLastQuery` is the snapshot of selectedFilters at the moment that
    // question was last sent — the empty-Send button is enabled only when the
    // current chips differ from that snapshot, so the button visually reflects
    // "there's a change to apply".
    const [lastUserQuestion, setLastUserQuestion] = useState<string>("");
    const [filtersAtLastQuery, setFiltersAtLastQuery] = useState<{ [key: string]: string[] }>({});
    // Snapshot of the answer mode at the moment the last question was sent.
    // The empty-Send button also arms when the mode differs from this, so
    // the user can re-run the same question under a newly-selected mode —
    // exactly mirroring the filter-change behavior above.
    const [answerModeAtLastQuery, setAnswerModeAtLastQuery] = useState<string>("");

    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const [useSemanticCaptions, setUseSemanticCaptions] =
        useState<boolean>(false);
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] =
        useState<boolean>(false);
    const [vectorFieldList, setVectorFieldList] = useState<VectorFieldOptions[]>([
        VectorFieldOptions.Embedding,
    ]);
    const [useOidSecurityFilter, setUseOidSecurityFilter] =
        useState<boolean>(false);
    const [useGroupsSecurityFilter, setUseGroupsSecurityFilter] =
        useState<boolean>(false);
    const [gpt4vInput, setGPT4VInput] = useState<GPT4VInput>(
        GPT4VInput.TextAndImages
    );
    const [useGPT4V, setUseGPT4V] = useState<boolean>(false);
    
    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<
        AnalysisPanelTabs | undefined
    >(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<
        [user: string, response: ChatAppResponse][]
    >([]);
    const [streamedAnswers, setStreamedAnswers] = useState<
        [user: string, response: ChatAppResponse][]
    >([]);
    const [speechUrls, setSpeechUrls] = useState<(string | null)[]>([]);
    const [streamingStatus, setStreamingStatus] = useState<string | null>(null);

    const [showGPT4VOptions, setShowGPT4VOptions] = useState<boolean>(false);
    const [showSemanticRankerOption, setShowSemanticRankerOption] =
        useState<boolean>(false);
    const [showVectorOption, setShowVectorOption] = useState<boolean>(false);
    const [showUserUpload, setShowUserUpload] = useState<boolean>(false);
    const [showLanguagePicker, setshowLanguagePicker] = useState<boolean>(false);
    const [showSpeechInput, setShowSpeechInput] = useState<boolean>(false);
    const [showSpeechOutputBrowser, setShowSpeechOutputBrowser] =
        useState<boolean>(false);
    const [showSpeechOutputAzure, setShowSpeechOutputAzure] =
        useState<boolean>(false);

    const [isHistoryVisible, setIsHistoryVisible] = useState(true);
    const [historyRefreshTrigger, setHistoryRefreshTrigger] = useState<number>(0); 
    const [isAnalysisPanelExpanded, setIsAnalysisPanelExpanded] = useState(false);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [selectedSourcefiles, setSelectedSourcefiles] = useState<SelectedFile[]>([]); // Selected files from Search page (with ID and name)

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    const client = useLogin ? useMsal().instance : undefined;
    const {loggedIn} = useContext(LoginContext);

    // Use custom hooks for better organization
    const { isAdminUser, isCheckingAdmin } = useAdminCheck();
    const { triggerHistorySave, shouldUpdateHistoryRef } = useChatHistory({
        sessionId,
        answers,
        selectedFilters,
        selectedSourcefiles,
        client,
        onHistorySaved: () => setHistoryRefreshTrigger(prev => prev + 1)
    });

    // Load filters and selected files from navigation state (passed from Search page)
    useEffect(() => {
        const navState = location.state as { 
            selectedFilters?: Record<string, string[]>;
            selectedSourcefiles?: SelectedFile[];
        } | null;
        
        if (navState) {
            if (navState.selectedFilters) {
                setSelectedFilters(navState.selectedFilters);
            }
            if (navState.selectedSourcefiles) {
                setSelectedSourcefiles(navState.selectedSourcefiles);
            }
            // Clear navigation state after reading
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    // Initialize audio once and clean up on unmount
    useEffect(() => {
        audioRef.current = new Audio();
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
                audioRef.current = null;
            }
        };
    }, []);

    const speechConfig: SpeechConfig = {
        speechUrls,
        setSpeechUrls,
        audio: audioRef.current!,
        isPlaying,
        setIsPlaying,
    };

    const getConfig = async () => {
        configApi().then((config) => {
            setShowGPT4VOptions(config.showGPT4VOptions);
            setUseSemanticRanker(config.showSemanticRankerOption);
            setShowSemanticRankerOption(config.showSemanticRankerOption);
            setShowVectorOption(config.showVectorOption);
            if (!config.showVectorOption) {
                setRetrievalMode(RetrievalMode.Text);
            }
            setShowUserUpload(config.showUserUpload);
            setshowLanguagePicker(config.showLanguagePicker);
            setShowSpeechInput(config.showSpeechInput);
            setShowSpeechOutputBrowser(config.showSpeechOutputBrowser);
            setShowSpeechOutputAzure(config.showSpeechOutputAzure);
        });
    };

    const initializeRouter = async () => {
        const client = useLogin ? useMsal().instance : undefined;
        setIsLoading(true);

        try {
            const idToken = client ? await getToken(client) : undefined;
            const response = await initializeSemanticRouter(idToken);

            if (!response.ok) {
                throw new Error("Failed to initialize semantic router");
            }

            const result = await response.json();

            // Update templates state if needed
            if (result.templates) {
                handleTemplateUpdate(result.templates);
            }

            setIsRouterInitialized(true);
        } catch (error) {
            console.error("Failed to initialize router:", error);
            // Optionally show error to user via your preferred method
            setError(error);
            setIsRouterInitialized(false);
        } finally {
            setIsLoading(false);
        }
    };
    // Toggle function for expand/collapse
    const toggleAnalysisPanelExpansion = () => {
        setIsAnalysisPanelExpanded(prev => !prev);
    };
    const handleAsyncRequest = async (
        question: string,
        currentAnswers: [string, ChatAppResponse][],
        responseBody: ReadableStream<any>
    ) => {
        let answer: string = "";
        let askResponse: ChatAppResponse = {} as ChatAppResponse;

        const updateState = (newContent: string) => {
            return new Promise((resolve) => {
                setTimeout(() => {
                    answer += newContent;
                    const latestResponse: ChatAppResponse = {
                        ...askResponse,
                        message: {content: answer, role: askResponse.message?.role},
                    };
                    setStreamedAnswers([...currentAnswers, [question, latestResponse]]);
                    resolve(null);
                }, 33);
            });
        };
        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                // Handle progress status updates (e.g., "Denke nach…", "Suche in Dokumenten…")
                if (event["status"] !== undefined) {
                    setStreamingStatus(event["status"]);
                }
                if (event["context"] && event["context"]["data_points"]) {
                    event["message"] = event["delta"];
                    askResponse = event as ChatAppResponse;
                } else if (event["delta"] && event["delta"]["content"] !== undefined) {
                    setIsLoading(false);
                    setStreamingStatus(null);  // Clear status once actual content arrives
                    await updateState(event["delta"]["content"]);
                } else if (event["context"]) {
                    // Update context with new keys from latest event
                    askResponse.context = {...askResponse.context, ...event["context"]};
                } else if (event["error"]) {
                    console.error("[handleAsyncRequest] Error event:", event["error"]);
                    throw Error(event["error"]);
                }
            }
        } finally {
            setIsStreaming(false);
            setStreamingStatus(null);
        }
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            message: {content: answer, role: askResponse.message?.role},
        };
        return fullResponse;
    };

    const makeFeedbackRequest = async (
        feedback: FeedbackPayload | undefined,
        question: string
      ) => {
        const token = client ? await getToken(client) : undefined;
      
        if (!feedback) {
          // Feedback retracted → send DELETE
          try {
            await deleteFeedbackApi(sessionId, question, token);
          } catch (e) {
            console.error("Failed to delete feedback", e);
          }
          return;
        }
      
        const payload = {
          ...feedback,
          question: question, // <-- typo fixed from 'quetion'
          sessionId: sessionId,
        };
      
        try {
          await feedbackApi(payload, token);
        } catch (e) {
          console.error("Failed to submit feedback", e);
        }
    };


    // Memoized helper function to format selected filters into readable text
    const formatFiltersText = useMemo(() => {
        return (filters: Record<string, string[]>): string => {
            if (!filters || Object.keys(filters).length === 0) {
                return "";
            }

            const filterParts: string[] = [];

            Object.entries(filters).forEach(([filterKey, values]) => {
                if (values && values.length > 0) {
                    // Use translation for filter name, fallback to the key if translation doesn't exist
                    const filterName = t(`searchFilter.${filterKey}`, filterKey);
                    const valuesList = values.join(", ");
                    filterParts.push(`${filterName}: ${valuesList}`);
                }
            });

            return filterParts.length > 0 ? ` [${filterParts.join(", ")}]` : "";
        };
    }, [t]);
    
    // Stable equality check for two `selectedFilters` objects. The chips order
    // doesn't matter — sort both sides before comparing so an added-then-
    // removed chip doesn't look "different".
    const filtersEqual = (
        a: { [key: string]: string[] },
        b: { [key: string]: string[] },
    ): boolean => {
        const ka = Object.keys(a).sort();
        const kb = Object.keys(b).sort();
        if (ka.length !== kb.length) return false;
        if (ka.some((k, i) => k !== kb[i])) return false;
        return ka.every(k => {
            const av = [...(a[k] || [])].sort();
            const bv = [...(b[k] || [])].sort();
            return av.length === bv.length && av.every((v, i) => v === bv[i]);
        });
    };

    const makeApiRequest = async (question: string) => {
        // Format and append selected filters to the question
        const filtersText = formatFiltersText(selectedFilters);
        const questionWithFilters = question + filtersText;

        lastQuestionRef.current = questionWithFilters;

        // Track this call as the "last query" so the empty-Send / filter-panel
        // "Anwenden" gate has consistent state regardless of how the query was
        // triggered (typed input, follow-up chip, retry, or empty-Send re-run).
        // A repeated call with the same question keeps `lastUserQuestion`
        // unchanged so the re-run paths keep pointing at the original.
        if (question !== lastUserQuestion) {
            setLastUserQuestion(question);
        }
        setFiltersAtLastQuery({ ...selectedFilters });
        setAnswerModeAtLastQuery(answerMode);

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        const token = client ? await getToken(client) : undefined;

        try {
            const messages: ResponseMessage[] = answers.flatMap((a, idx) => [
                {content: a[0], role: "user"},
                {content: a[1]?.message?.content, role: "assistant"},
            ]);

            const request: ChatAppRequest = {
                messages: [...messages, {content: questionWithFilters, role: "user"}],
                context: {
                    overrides: {
                        prompt_template:
                            promptTemplate.length === 0 ? undefined : promptTemplate,
                        exclude_category:
                            excludeCategory.length === 0 ? undefined : excludeCategory,
                        top: retrieveCount,
                        temperature: temperature,
                        answer_mode: answerMode,
                        minimum_reranker_score: minimumRerankerScore,
                        minimum_search_score: minimumSearchScore,
                        retrieval_mode: retrievalMode,
                        semantic_ranker: useSemanticRanker,
                        semantic_captions: useSemanticCaptions,
                        semantic_routing: useSemanticRouting,
                        suggest_followup_questions: useSuggestFollowupQuestions,
                        use_oid_security_filter: useOidSecurityFilter,
                        use_groups_security_filter: useGroupsSecurityFilter,
                        vector_fields: vectorFieldList,
                        use_gpt4v: useGPT4V,
                        gpt4v_input: gpt4vInput,
                        selectedFilters: selectedFilters,
                        selectedSourcefileIds: selectedSourcefiles.length > 0 ? selectedSourcefiles.map(f => f.id) : undefined,
                        language: i18n.language,
                        ...(seed !== null ? {seed: seed} : {}),
                    },
                },
                // AI Chat Protocol: Client must pass on any session state received from the server
                session_state: answers.length
                    ? answers[answers.length - 1][1]?.session_state
                    : null,
            };        

            const response = await chatApi(request, shouldStream, token);
            if (!response.body) {
                throw Error("No response body");
            }
            if (response.status > 299 || !response.ok) {
                throw Error(`Request failed with status ${response.status}`);
            }
            if (shouldStream) {
                const parsedResponse: ChatAppResponse = await handleAsyncRequest(
                    questionWithFilters,
                    answers,
                    response.body
                );
                triggerHistorySave();
                setAnswers([...answers, [questionWithFilters, parsedResponse]]);
            } else {
                const parsedResponse: ChatAppResponseOrError = await response.json();
                if (parsedResponse.error) {
                    throw Error(parsedResponse.error);
                }
                triggerHistorySave();
                setAnswers([...answers, [questionWithFilters, parsedResponse as ChatAppResponse]]);
            }
            setSpeechUrls([...speechUrls, null]);
        } catch (e) {
            console.error("[makeApiRequest] Error:", e);
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = () => {
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setSpeechUrls([]);
        setStreamedAnswers([]);
        setIsLoading(false);
        setIsStreaming(false);
        setSessionId(uuidv4());
        setSelectedFilters({});
        setSelectedSourcefiles([]); // Clear selected files when starting new chat
        shouldUpdateHistoryRef.current = false;
    };

    // NOTE: braces around the effect body are REQUIRED. An arrow function
    // without braces implicitly returns its expression — here that would be
    // whatever `scrollIntoView(...)` returns. React stores an effect's return
    // value as its cleanup function. Chromium 150 (Edge 150 first, Chrome will
    // follow) started returning a non-undefined, non-function value from
    // scrollIntoView in some cases; on the next re-run React tries to invoke
    // that as the cleanup and throws `TypeError: n is not a function` deep in
    // the reconciler. Braces make the effect return void, which is safe.
    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({behavior: "smooth"});
    }, [isLoading]);
    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({behavior: "auto"});
    }, [streamedAnswers]);
    useEffect(() => {
        getConfig();
    }, []);

    const onPromptTemplateChange = (
        _ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ) => {
        setPromptTemplate(newValue || "");
    };

    const onTemperatureChange = (
        _ev?: React.SyntheticEvent<HTMLElement, Event>,
        newValue?: string
    ) => {
        setTemperature(parseFloat(newValue || "0"));
    };

    const onSeedChange = (
        _ev?: React.SyntheticEvent<HTMLElement, Event>,
        newValue?: string
    ) => {
        setSeed(parseInt(newValue || ""));
    };

    const onMinimumSearchScoreChange = (
        _ev?: React.SyntheticEvent<HTMLElement, Event>,
        newValue?: string
    ) => {
        setMinimumSearchScore(parseFloat(newValue || "0"));
    };

    const onMinimumRerankerScoreChange = (
        _ev?: React.SyntheticEvent<HTMLElement, Event>,
        newValue?: string
    ) => {
        setMinimumRerankerScore(parseFloat(newValue || "0"));
    };

    const onRetrieveCountChange = (
        _ev?: React.SyntheticEvent<HTMLElement, Event>,
        newValue?: string
    ) => {
        setRetrieveCount(parseInt(newValue || "15"));
    };

    const onUseSemanticRankerChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) => {
        setUseSemanticRanker(!!checked);
    };

    const onUseSemanticRoutingChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) => {
        setUseSemanticRouting(!!checked);
    };

    const onUseSemanticCaptionsChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) => {
        setUseSemanticCaptions(!!checked);
    };

    const onShouldStreamChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) => {
        setShouldStream(!!checked);
    };

    const onExcludeCategoryChanged = (
        _ev?: React.FormEvent,
        newValue?: string
    ) => {
        setExcludeCategory(newValue || "");
    };

    const onUseSuggestFollowupQuestionsChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) => {
        setUseSuggestFollowupQuestions(!!checked);
    };

    const onUseOidSecurityFilterChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) => {
        setUseOidSecurityFilter(!!checked);
    };

    const onUseGroupsSecurityFilterChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) => {
        setUseGroupsSecurityFilter(!!checked);
    };

    const [prefillQuestion, setPrefillQuestion] = useState<string>("");

    const onExampleClicked = (example: string) => {
        setPrefillQuestion(example);
    };

    const onShowCitation = (citation: string, index: number) => {
        if (
            activeCitation === citation &&
            activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab &&
            selectedAnswer === index
        ) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onShowFileDocument = (fileUrl: string) => {
        if (!fileUrl) return;
        
        if (activeCitation === fileUrl && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(fileUrl);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    // IDs for form labels and their associated callouts
    const promptTemplateId = useId("promptTemplate");
    const promptTemplateFieldId = useId("promptTemplateField");
    const temperatureId = useId("temperature");
    const temperatureFieldId = useId("temperatureField");
    const seedId = useId("seed");
    const seedFieldId = useId("seedField");
    const searchScoreId = useId("searchScore");
    const searchScoreFieldId = useId("searchScoreField");
    const rerankerScoreId = useId("rerankerScore");
    const rerankerScoreFieldId = useId("rerankerScoreField");
    const retrieveCountId = useId("retrieveCount");
    const retrieveCountFieldId = useId("retrieveCountField");
    const excludeCategoryId = useId("excludeCategory");
    const excludeCategoryFieldId = useId("excludeCategoryField");
    const semanticRankerId = useId("semanticRanker");
    const semanticRankerFieldId = useId("semanticRankerField");
    const semanticCaptionsId = useId("semanticCaptions");
    const semanticCaptionsFieldId = useId("semanticCaptionsField");
    const suggestFollowupQuestionsId = useId("suggestFollowupQuestions");
    const suggestFollowupQuestionsFieldId = useId(
        "suggestFollowupQuestionsField"
    );
    const useOidSecurityFilterId = useId("useOidSecurityFilter");
    const useOidSecurityFilterFieldId = useId("useOidSecurityFilterField");
    const useGroupsSecurityFilterId = useId("useGroupsSecurityFilter");
    const useGroupsSecurityFilterFieldId = useId("useGroupsSecurityFilterField");
    const shouldStreamId = useId("shouldStream");
    const shouldStreamFieldId = useId("shouldStreamField");

    return (
        <ChatContext.Provider value={{ clearChat }}>
        <div className={"flex w-full h-full grow overflow-hidden relative"}>
            {/* Setting the page title using react-helmet-async */}
            <Helmet>
                <title>{t("pageTitle")}</title>
            </Helmet>
            {isHistoryVisible ? (<>
                <div
                    className={
                        "shrink w-96 bg-white border-r shadow flex flex-col h-full overflow-hidden"
                    }
                >

                    <div className="grow shrink h-full overflow-y-auto min-h-0">
                        <ChatHistory
                            currentSessionId={sessionId}
                            clearChat={clearChat}
                            isHistoryVisible={isHistoryVisible}
                            onClose={() => setIsHistoryVisible(false)}
                            onHistorySaved={historyRefreshTrigger}
                            setContext={(
                                newContext: any,
                                newSessionId: string,
                                restoredFilters?: Record<string, string[]>,
                                restoredSourcefiles?: SelectedFile[]
                            ) => {
                                clearChat();
                                lastQuestionRef.current = "Something";

                                setSessionId(newSessionId);
                                setAnswers(newContext);

                                // Restore filters and selected files from saved chat
                                const filtersToRestore = restoredFilters ?? {};
                                setSelectedFilters(filtersToRestore);
                                if (restoredSourcefiles && restoredSourcefiles.length > 0) {
                                    setSelectedSourcefiles(restoredSourcefiles);
                                } else {
                                    setSelectedSourcefiles([]);
                                }

                                // Seed the empty-Send bookkeeping from the last
                                // user turn in the restored session, so the user
                                // can immediately hit "Anwenden" / empty-Send
                                // after changing a filter chip — otherwise
                                // `lastUserQuestion` is "" and both buttons stay
                                // greyed out.
                                if (Array.isArray(newContext) && newContext.length > 0) {
                                    const lastTurn = newContext[newContext.length - 1];
                                    const rawQuestion: string = Array.isArray(lastTurn) ? (lastTurn[0] || "") : "";
                                    // Strip the trailing " [...]" filter suffix
                                    // that was baked into the stored question so
                                    // makeApiRequest can re-append current filters
                                    // cleanly on the next re-run.
                                    const bareQuestion = rawQuestion.replace(/\s*\[[^\]]*\]\s*$/, "");
                                    setLastUserQuestion(bareQuestion);
                                } else {
                                    setLastUserQuestion("");
                                }
                                setFiltersAtLastQuery({ ...filtersToRestore });
                                // Seed the mode snapshot to the current mode so a
                                // freshly-restored chat doesn't show the Send
                                // button as armed until the user actually changes
                                // the Antwortmodus.
                                setAnswerModeAtLastQuery(answerMode);

                                shouldUpdateHistoryRef.current = false; // <--- This is the fix
                            }}
                        />
                    </div>


                    <div className={"shrink"}>
                        {/*<ClearChatButton*/}
                        {/*  className={styles.commandButton}*/}
                        {/*  onClick={clearChat}*/}
                        {/*  disabled={!lastQuestionRef.current || isLoading}*/}
                        {/*/>*/}
                        {showUserUpload && (
                            <UploadFile className={styles.commandButton} disabled={!loggedIn}/>
                        )}
                        {isAdminUser && !isCheckingAdmin && (
                            <>
                                <SettingsButton
                                    className={styles.commandButton}
                                    onClick={() => setIsConfigPanelOpen(!isConfigPanelOpen)}
                                />
                                <AdminAccessButton
                                    className={styles.commandButton}
                                    onClick={() => navigate("/admin")}
                                />
                            </>
                        )}
                    </div>
                </div>
            </>) : (<>
                <div className={"shrink w-10"}>


                    <div className={"bg-white shadow rounded-r-md mt-10 flex justify-center items-center"}>
                        <Button
                            appearance="subtle"
                            icon={<ChevronDoubleRight16Regular/>}
                            onClick={() => setIsHistoryVisible(true)}
                            className={"h-14 w-14"}
                            title={t("labels.openChatHistory")}
                        />
                    </div>
                </div>
            </>)}
            <div className={"grow h-full flex flex-col mx-auto max-w-5xl max-h-full overflow-hidden"}>
                <>
                    {!lastQuestionRef.current ? (
                        <div
                            className={"h-full py-5 grow flex flex-col items-center m-5 gap-2 shrink overflow-y-auto "}
                        >
                            <img
                                src={mainImage}
                                className={"w-32 h-32 mb-5"}
                                aria-hidden="true"
                                aria-label="Chat logo"
                            />
                            <h1 className={"text-[#009835] text-4xl bold"}>
                                {t("chatEmptyStateTitle")}
                            </h1>
                            <h2 className={styles.chatEmptyStateSubtitle}>
                                {t("chatEmptyStateSubtitle")}
                            </h2>
                            {showLanguagePicker && (
                                <LanguagePicker
                                    onLanguageChange={(newLang) => i18n.changeLanguage(newLang)}
                                />
                            )}

                            <ExampleList
                                onExampleClicked={onExampleClicked}
                                useGPT4V={useGPT4V}
                            />
                        </div>
                    ) : (
                        <div className={"h-full py-5 grow shrink overflow-y-auto mx-2"}>
                            {isStreaming &&
                                streamedAnswers.map((streamedAnswer, index) => (
                                    <div key={`${sessionId}-stream-${index}`}>
                                        <UserChatMessage message={streamedAnswer[0]}/>
                                        <Answer
                                            isStreaming={true}
                                            key={`${sessionId}-stream-${index}`}
                                            answer={streamedAnswer[1]}
                                            index={index}
                                            sessionId={sessionId}
                                            speechConfig={speechConfig}
                                            isSelected={false}
                                            onFeedbackClicked={(feedback) =>
                                                makeFeedbackRequest(feedback, streamedAnswer[0])
                                            }
                                            onCitationClicked={(c) => onShowCitation(c, index)}
                                            onThoughtProcessClicked={() =>
                                                onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)
                                            }
                                            onSupportingContentClicked={() =>
                                                onToggleTab(
                                                    AnalysisPanelTabs.SupportingContentTab,
                                                    index
                                                )
                                            }
                                            onFollowupQuestionClicked={(q) => makeApiRequest(q)}
                                            showFollowupQuestions={
                                                useSuggestFollowupQuestions &&
                                                answers.length - 1 === index
                                            }
                                            showSpeechOutputAzure={showSpeechOutputAzure}
                                            showSpeechOutputBrowser={showSpeechOutputBrowser}
                                        />
                                    </div>
                                ))}
                            {!isStreaming &&
                                answers.map((answer, index) => (
                                    <div key={`${sessionId}-${index}`}>
                                        <UserChatMessage message={answer[0]}/>
                                        <Answer
                                            isStreaming={false}
                                            key={`${sessionId}-${index}`}
                                            answer={answer[1]}
                                            index={index}
                                            sessionId={sessionId}
                                            speechConfig={speechConfig}
                                            onFeedbackClicked={(feedback) =>
                                                makeFeedbackRequest(feedback, answer[0])
                                            }
                                            isSelected={
                                                selectedAnswer === index &&
                                                activeAnalysisPanelTab !== undefined
                                            }
                                            onCitationClicked={(c) => onShowCitation(c, index)}
                                            onThoughtProcessClicked={() =>
                                                onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)
                                            }
                                            onSupportingContentClicked={() =>
                                                onToggleTab(
                                                    AnalysisPanelTabs.SupportingContentTab,
                                                    index
                                                )
                                            }
                                            onFollowupQuestionClicked={(q) => makeApiRequest(q)}
                                            showFollowupQuestions={
                                                useSuggestFollowupQuestions &&
                                                answers.length - 1 === index
                                            }
                                            showSpeechOutputAzure={showSpeechOutputAzure}
                                            showSpeechOutputBrowser={showSpeechOutputBrowser}
                                        />
                                    </div>
                                ))}
                            {isLoading && (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current}/>
                                    <div className={styles.chatMessageGptMinWidth}>
                                        {streamingStatus ? (
                                            <div style={{padding: "12px 16px", fontStyle: "italic", color: "#666", fontSize: "14px"}}>
                                                {streamingStatus}
                                            </div>
                                        ) : (
                                            <AnswerLoading/>
                                        )}
                                    </div>
                                </>
                            )}
                            {error ? (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current}/>
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError
                                            error={error.toString()}
                                            onRetry={() => makeApiRequest(lastQuestionRef.current)}
                                        />
                                    </div>
                                </>
                            ) : null}
                            <div ref={chatMessageStreamEnd}/>
                        </div>
                    )}

                    <div className={"my-4 shrink-0 mx-2"}>
                        {/* Salesforce-driven access filter chips (HolzHer + product groups).
                            Renders nothing for non-SF users. */}
                        <SalesforceFilterChips />

                        {/* User-selected filter chips (removable) */}
                        {Object.entries(selectedFilters).some(([_, vals]) => vals.length > 0) && (
                            <div className={styles.selectedFilesContainer}>
                                {Object.entries(selectedFilters).flatMap(([category, values]) =>
                                    values.map((value) => (
                                        <div key={`${category}-${value}`} className={styles.selectedFilePill}>
                                            <span className={styles.selectedFileName} title={`${category}: ${value}`}>
                                                {displayFacetValue(category, value)}
                                            </span>
                                            <button
                                                className={styles.removeFileButton}
                                                onClick={() => {
                                                    setSelectedFilters((prev) => {
                                                        const updated = { ...prev };
                                                        updated[category] = updated[category].filter((v) => v !== value);
                                                        if (updated[category].length === 0) delete updated[category];
                                                        return updated;
                                                    });
                                                }}
                                                title={`Remove ${value}`}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* Selected files pills - above input */}
                        {selectedSourcefiles.length > 0 && (
                            <div className={styles.selectedFilesContainer} title={t("labels.selectedFiles.title")}>
                                {selectedSourcefiles.map((file) => (
                                    <div key={file.id} className={styles.selectedFilePill}>
                                        <span 
                                            className={styles.selectedFileName} 
                                            title={file.url ? t("search.openDocument") : file.id}
                                            onClick={() => file.url && onShowFileDocument(file.url)}
                                            style={{ cursor: file.url ? 'pointer' : 'default' }}
                                        >
                                            {file.name}
                                        </span>
                                        <button
                                            className={styles.removeFileButton}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedSourcefiles(prev => prev.filter(f => f.id !== file.id));
                                            }}
                                            title={t("labels.selectedFiles.removeFile")}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={{display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", justifyContent: "flex-end"}}>
                            <label style={{fontSize: "12px", color: "#666"}}>Antwortmodus:</label>
                            <select
                                value={answerMode}
                                onChange={(e) => setAnswerMode(e.target.value)}
                                style={{padding: "4px 8px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "13px"}}
                            >
                                <option value="quick">Schnelle Antwort</option>
                                <option value="auto">Auto</option>
                                <option value="deep">Tiefere Analyse</option>
                            </select>
                        </div>
                        <QuestionInput
                            clearOnSend
                            placeholder={t("defaultExamples.placeholder")}
                            disabled={isLoading}
                            initQuestion={prefillQuestion}
                            onSend={(question) => {
                                const trimmed = question.trim();
                                if (!trimmed) {
                                    // Empty Send: re-run the user's last actual
                                    // question with whatever filters are now set.
                                    // makeApiRequest handles the bookkeeping —
                                    // same question keeps `lastUserQuestion`,
                                    // and `filtersAtLastQuery` is snapshotted
                                    // to the current selection so the button
                                    // greys out again until the user changes
                                    // a chip.
                                    if (!lastUserQuestion) return;
                                    makeApiRequest(lastUserQuestion);
                                } else {
                                    // Real new question — makeApiRequest records
                                    // it and the filter snapshot.
                                    makeApiRequest(question);
                                }
                                setPrefillQuestion("");
                            }}
                            showSpeechInput={showSpeechInput}
                            onFilterClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                            isFilterActive={Object.keys(selectedFilters).length > 0}
                            // Send button stays disabled until there's something
                            // to re-run differently — meaning a prior question
                            // exists AND filters have changed since then.
                            allowEmptySend={
                                !!lastUserQuestion &&
                                (!filtersEqual(selectedFilters, filtersAtLastQuery) ||
                                    answerMode !== answerModeAtLastQuery)
                            }
                        />
                    </div>
                </>

                <Panel
                    headerText={t("labels.headerText")}
                    isOpen={isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => setIsConfigPanelOpen(false)}
                    closeButtonAriaLabel={t("labels.closeButton")}
                    onRenderFooterContent={() => (
                        <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>
                            {t("labels.closeButton")}
                        </DefaultButton>
                    )}
                    isFooterAtBottom={true}
                >
                    <TextField
                        id={promptTemplateFieldId}
                        className={styles.chatSettingsSeparator}
                        defaultValue={promptTemplate}
                        label={t("labels.promptTemplate")}
                        multiline
                        autoAdjustHeight
                        onChange={onPromptTemplateChange}
                        aria-labelledby={promptTemplateId}
                        onRenderLabel={(props: ITextFieldProps | undefined) => (
                            <HelpCallout
                                labelId={promptTemplateId}
                                fieldId={promptTemplateFieldId}
                                helpText={t("helpTexts.promptTemplate")}
                                label={props?.label}
                            />
                        )}
                    />

                    <TextField
                        id={temperatureFieldId}
                        className={styles.chatSettingsSeparator}
                        label={t("labels.temperature")}
                        type="number"
                        min={0}
                        max={1}
                        step={0.1}
                        defaultValue={temperature.toString()}
                        onChange={onTemperatureChange}
                        aria-labelledby={temperatureId}
                        onRenderLabel={(props: ITextFieldProps | undefined) => (
                            <HelpCallout
                                labelId={temperatureId}
                                fieldId={temperatureFieldId}
                                helpText={t("helpTexts.temperature")}
                                label={props?.label}
                            />
                        )}
                    />

                    <TextField
                        id={seedFieldId}
                        className={styles.chatSettingsSeparator}
                        label={t("labels.seed")}
                        type="text"
                        defaultValue={seed?.toString() || ""}
                        onChange={onSeedChange}
                        aria-labelledby={seedId}
                        onRenderLabel={(props: ITextFieldProps | undefined) => (
                            <HelpCallout
                                labelId={seedId}
                                fieldId={seedFieldId}
                                helpText={t("helpTexts.seed")}
                                label={props?.label}
                            />
                        )}
                    />

                    <TextField
                        id={searchScoreFieldId}
                        className={styles.chatSettingsSeparator}
                        label={t("labels.minimumSearchScore")}
                        type="number"
                        min={0}
                        step={0.01}
                        defaultValue={minimumSearchScore.toString()}
                        onChange={onMinimumSearchScoreChange}
                        aria-labelledby={searchScoreId}
                        onRenderLabel={(props: ITextFieldProps | undefined) => (
                            <HelpCallout
                                labelId={searchScoreId}
                                fieldId={searchScoreFieldId}
                                helpText={t("helpTexts.searchScore")}
                                label={props?.label}
                            />
                        )}
                    />

                    {showSemanticRankerOption && (
                        <TextField
                            id={rerankerScoreFieldId}
                            className={styles.chatSettingsSeparator}
                            label={t("labels.minimumRerankerScore")}
                            type="number"
                            min={1}
                            max={4}
                            step={0.1}
                            defaultValue={minimumRerankerScore.toString()}
                            onChange={onMinimumRerankerScoreChange}
                            aria-labelledby={rerankerScoreId}
                            onRenderLabel={(props: ITextFieldProps | undefined) => (
                                <HelpCallout
                                    labelId={rerankerScoreId}
                                    fieldId={rerankerScoreFieldId}
                                    helpText={t("helpTexts.rerankerScore")}
                                    label={props?.label}
                                />
                            )}
                        />
                    )}

                    <TextField
                        id={retrieveCountFieldId}
                        className={styles.chatSettingsSeparator}
                        label={t("labels.retrieveCount")}
                        type="number"
                        min={1}
                        max={50}
                        defaultValue={retrieveCount.toString()}
                        onChange={onRetrieveCountChange}
                        aria-labelledby={retrieveCountId}
                        onRenderLabel={(props: ITextFieldProps | undefined) => (
                            <HelpCallout
                                labelId={retrieveCountId}
                                fieldId={retrieveCountFieldId}
                                helpText={t("helpTexts.retrieveNumber")}
                                label={props?.label}
                            />
                        )}
                    />

                    <TextField
                        id={excludeCategoryFieldId}
                        className={styles.chatSettingsSeparator}
                        label={t("labels.excludeCategory")}
                        defaultValue={excludeCategory}
                        onChange={onExcludeCategoryChanged}
                        aria-labelledby={excludeCategoryId}
                        onRenderLabel={(props: ITextFieldProps | undefined) => (
                            <HelpCallout
                                labelId={excludeCategoryId}
                                fieldId={excludeCategoryFieldId}
                                helpText={t("helpTexts.excludeCategory")}
                                label={props?.label}
                            />
                        )}
                    />

                    {showSemanticRankerOption && (
                        <>
                            <Checkbox
                                id={semanticRankerFieldId}
                                className={styles.chatSettingsSeparator}
                                checked={useSemanticRanker}
                                label={t("labels.useSemanticRanker")}
                                onChange={onUseSemanticRankerChange}
                                aria-labelledby={semanticRankerId}
                                onRenderLabel={(props: ICheckboxProps | undefined) => (
                                    <HelpCallout
                                        labelId={semanticRankerId}
                                        fieldId={semanticRankerFieldId}
                                        helpText={t("helpTexts.useSemanticReranker")}
                                        label={props?.label}
                                    />
                                )}
                            />

                            <Checkbox
                                id={semanticCaptionsFieldId}
                                className={styles.chatSettingsSeparator}
                                checked={useSemanticCaptions}
                                label={t("labels.useSemanticCaptions")}
                                onChange={onUseSemanticCaptionsChange}
                                disabled={!useSemanticRanker}
                                aria-labelledby={semanticCaptionsId}
                                onRenderLabel={(props: ICheckboxProps | undefined) => (
                                    <HelpCallout
                                        labelId={semanticCaptionsId}
                                        fieldId={semanticCaptionsFieldId}
                                        helpText={t("helpTexts.useSemanticCaptions")}
                                        label={props?.label}
                                    />
                                )}
                            />
                        </>
                    )}
                    <SemanticRoutingControl
                        useSemanticRouting={useSemanticRouting}
                        setUseSemanticRouting={setUseSemanticRouting}
                        setTemplates={setTemplates}
                        setIsRouterInitialized={setIsRouterInitialized}
                    />
                    <TemplateForm
                        initialTemplates={templates}
                        onTemplateUpdate={handleTemplateUpdate}
                        useSemanticTemplate={useSemanticTemplate}
                        setUseSemanticTemplate={setUseSemanticTemplate}
                        isRouterInitialized={isRouterInitialized} // Pass initialization state
                    />
                    <Checkbox
                        id={suggestFollowupQuestionsFieldId}
                        className={styles.chatSettingsSeparator}
                        checked={useSuggestFollowupQuestions}
                        label={t("labels.useSuggestFollowupQuestions")}
                        onChange={onUseSuggestFollowupQuestionsChange}
                        aria-labelledby={suggestFollowupQuestionsId}
                        onRenderLabel={(props: ICheckboxProps | undefined) => (
                            <HelpCallout
                                labelId={suggestFollowupQuestionsId}
                                fieldId={suggestFollowupQuestionsFieldId}
                                helpText={t("helpTexts.suggestFollowupQuestions")}
                                label={props?.label}
                            />
                        )}
                    />
                    {showGPT4VOptions && (
                        <GPT4VSettings
                            gpt4vInputs={gpt4vInput}
                            isUseGPT4V={useGPT4V}
                            updateUseGPT4V={(useGPT4V) => {
                                setUseGPT4V(useGPT4V);
                            }}
                            updateGPT4VInputs={(inputs) => setGPT4VInput(inputs)}
                        />
                    )}
                    {showVectorOption && (
                        <VectorSettings
                            defaultRetrievalMode={retrievalMode}
                            showImageOptions={useGPT4V && showGPT4VOptions}
                            updateVectorFields={(options: VectorFieldOptions[]) =>
                                setVectorFieldList(options)
                            }
                            updateRetrievalMode={(retrievalMode: RetrievalMode) =>
                                setRetrievalMode(retrievalMode)
                            }
                        />
                    )}
                    {useLogin && (
                        <>
                            <Checkbox
                                id={useOidSecurityFilterFieldId}
                                className={styles.chatSettingsSeparator}
                                checked={useOidSecurityFilter || requireAccessControl}
                                label={t("labels.useOidSecurityFilter")}
                                disabled={!loggedIn || requireAccessControl}
                                onChange={onUseOidSecurityFilterChange}
                                aria-labelledby={useOidSecurityFilterId}
                                onRenderLabel={(props: ICheckboxProps | undefined) => (
                                    <HelpCallout
                                        labelId={useOidSecurityFilterId}
                                        fieldId={useOidSecurityFilterFieldId}
                                        helpText={t("helpTexts.useOidSecurityFilter")}
                                        label={props?.label}
                                    />
                                )}
                            />
                            <Checkbox
                                id={useGroupsSecurityFilterFieldId}
                                className={styles.chatSettingsSeparator}
                                checked={useGroupsSecurityFilter || requireAccessControl}
                                label={t("labels.useGroupsSecurityFilter")}
                                disabled={!loggedIn || requireAccessControl}
                                onChange={onUseGroupsSecurityFilterChange}
                                aria-labelledby={useGroupsSecurityFilterId}
                                onRenderLabel={(props: ICheckboxProps | undefined) => (
                                    <HelpCallout
                                        labelId={useGroupsSecurityFilterId}
                                        fieldId={useGroupsSecurityFilterFieldId}
                                        helpText={t("helpTexts.useGroupsSecurityFilter")}
                                        label={props?.label}
                                    />
                                )}
                            />
                        </>
                    )}
                    <Checkbox
                        id={shouldStreamFieldId}
                        className={styles.chatSettingsSeparator}
                        checked={shouldStream}
                        label={t("labels.shouldStream")}
                        onChange={onShouldStreamChange}
                        aria-labelledby={shouldStreamId}
                        onRenderLabel={(props: ICheckboxProps | undefined) => (
                            <HelpCallout
                                labelId={shouldStreamId}
                                fieldId={shouldStreamFieldId}
                                helpText={t("helpTexts.streamChat")}
                                label={props?.label}
                            />
                        )}
                    />
                    {useLogin && <TokenClaimsDisplay/>}
                </Panel>

                {/* Filter Panel */}
                <Panel
                    headerText={t("labels.filter")}
                    isOpen={isFilterPanelOpen}
                    isBlocking={false}
                    onDismiss={() => setIsFilterPanelOpen(false)}
                    closeButtonAriaLabel={t("labels.closeButton")}
                    onRenderFooterContent={() => {
                        const hasFiltersSelected = Object.values(selectedFilters).some(arr => arr.length > 0);
                        // "Anwenden" is enabled when there's something to re-run
                        // differently — the user has asked a question before AND
                        // the current filter set differs from when they asked it.
                        // (Removing all chips counts as a change, so the button
                        // is reachable in that path too.)
                        const isApplyEnabled =
                            !!lastUserQuestion &&
                            !filtersEqual(selectedFilters, filtersAtLastQuery);
                        
                        const handleApplyFilters = () => {
                            // Re-run the user's last actual question with the
                            // new filter set. Same logic as the empty-Send
                            // path — makeApiRequest handles the filter
                            // snapshot so the button greys out again after
                            // the click.
                            if (!lastUserQuestion) return;
                            makeApiRequest(lastUserQuestion);
                            setIsFilterPanelOpen(false);
                        };

                        const handleSearchDocuments = () => {
                            // Pass filters via navigation state
                            navigate("/search", { 
                                state: { 
                                    selectedFilters: selectedFilters 
                                } 
                            });
                            setIsFilterPanelOpen(false);
                        };
                        
                        return (
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                gap: '8px',
                                backgroundColor: 'white',
                                padding: '16px',
                                borderTop: '1px solid #e1dfdd',
                                marginBottom: '-1px',
                                position: 'relative',
                                zIndex: 1,
                                boxShadow: '0 -4px 8px rgba(0, 0, 0, 0.1)'
                            }}>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <DefaultButton onClick={() => setIsFilterPanelOpen(false)}>
                                        {t("labels.closeButton")}
                                    </DefaultButton>
                                    <DefaultButton 
                                        onClick={handleApplyFilters}
                                        disabled={!isApplyEnabled}
                                        primary
                                    >
                                        {t("labels.applyButton")}
                                    </DefaultButton>
                                </div>
                                <DefaultButton 
                                    onClick={handleSearchDocuments}
                                    disabled={!hasFiltersSelected}
                                    style={{ width: '100%' }}
                                >
                                    {t("labels.useFiltersToSearch") || "Use filters to search documents"}
                                </DefaultButton>
                            </div>
                        );
                    }}
                    isFooterAtBottom={true}
                >
                    <div style={{ paddingBottom: '120px' }}>
                        <SearchFilter
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                            hideButton={true}
                        />
                    </div>
                </Panel>
            </div>

            {/* Analysis Panel */}
            {activeAnalysisPanelTab && (
                <AnalysisPanel
                className={`${styles.chatAnalysisPanel}`}
                activeCitation={activeCitation}
                onActiveTabChanged={(x) => onToggleTab(x, selectedAnswer)}
                citationHeight="1200px"
                answer={answers.length > 0 ? answers[selectedAnswer][1] : {
                    message: { content: "", role: "assistant" },
                    delta: { content: "", role: "assistant" },
                    context: { data_points: [], followup_questions: null, thoughts: [] },
                    session_state: null
                }}
                activeTab={activeAnalysisPanelTab}
                isExpanded={isAnalysisPanelExpanded}
                onToggleExpand={toggleAnalysisPanelExpansion}
                onClose={() => {
                    setActiveAnalysisPanelTab(undefined);
                    setActiveCitation(undefined);
                }}
                />
            )}
        </div>
        </ChatContext.Provider>
    );
};

export default Chat;
